/**
 * **The part of a task block that waits on a read** — one shape for every block that reads something
 * of its own. While the gate waits: the block's reserved box (its own height, `reserve`), the bars
 * inside it once the skeleton is due, and the words a screen reader hears instead. A failed read is
 * said in one line; a read with nothing to draw draws nothing; otherwise the reading, on
 * `gate.reveal`.
 *
 * The block keeps `aria-busy` on its own box, which also holds what stands under the reading.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import styles from './taskBlocks.module.css';

interface BlockReadProps {
  readonly gate: SkeletonGate;
  /** The block's reserve class — the height its reading will take. */
  readonly reserve: string | undefined;
  readonly failed?: boolean;
  /** A sentence drawn in the reading's place, where the read answered with nothing to draw. */
  readonly note?: string | null;
  /** The reading's own box class, beside `gate.reveal`. */
  readonly className?: string;
  /** The reading; `null` while there is nothing to draw. */
  readonly children: ReactNode;
}

export function BlockRead({ gate, reserve, failed = false, note = null, className, children }: BlockReadProps) {
  const { t } = useTranslation();
  if (gate.waiting) {
    return (
      <>
        <span className={`${String(styles.reserve)} ${String(reserve)} ${gate.skeleton ? String(skel.bar) : ''}`} />
        <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
      </>
    );
  }
  if (failed || note !== null) {
    return (
      <p className={`${String(styles.note)} ${gate.reveal}`}>{failed ? t('web:tasks.blocks.readFailed') : note}</p>
    );
  }
  if (children === null) return null;
  return <div className={className === undefined ? gate.reveal : `${className} ${gate.reveal}`}>{children}</div>;
}
